import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Clock, Target } from 'lucide-react';

interface OnboardingModalProps {
  isOpen: boolean;
  onComplete: () => void;
}

const OnboardingModal: React.FC<OnboardingModalProps> = ({ isOpen, onComplete }) => {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [dailyTarget, setDailyTarget] = useState('240'); // 4 hours default
  const [loading, setLoading] = useState(false);

  const targetOptions = [
    { value: '60', label: '1 hour' },
    { value: '120', label: '2 hours' },
    { value: '180', label: '3 hours' },
    { value: '240', label: '4 hours' },
    { value: '300', label: '5 hours' },
    { value: '360', label: '6 hours' },
    { value: '480', label: '8 hours' },
  ];

  const handleNext = () => {
    if (step === 1 && name.trim()) {
      setStep(2);
    }
  };

  const handleComplete = async () => {
    if (!user || !name.trim()) return;

    setLoading(true);
    try {
      // Update the profiles table
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          name: name.trim(),
          daily_focus_target: parseInt(dailyTarget)
        })
        .eq('id', user.id);

      if (profileError) {
        console.error('Error updating profile:', profileError);
        toast.error('Failed to save your preferences');
        return;
      }

      // Also update the user metadata to keep them in sync
      const { error: userError } = await supabase.auth.updateUser({
        data: {
          name: name.trim(),
          display_name: name.trim()
        }
      });

      if (userError) {
        console.error('Error updating user metadata:', userError);
        // Don't show error to user since profile was saved successfully
        console.log('Profile saved but user metadata update failed');
      }

      toast.success('Welcome! Your profile has been set up.');
      onComplete();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-center">
            {step === 1 ? 'Welcome! Let\'s get started' : 'Set your focus goal'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {step === 1 ? (
            <div className="space-y-4">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-primary" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Help us personalize your experience
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium">
                  What should we call you?
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                  className="h-11"
                  autoFocus
                />
              </div>

              <Button 
                onClick={handleNext}
                className="w-full h-11 font-medium" 
                disabled={!name.trim()}
              >
                Continue
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-primary" />
                </div>
                <p className="text-sm text-muted-foreground">
                  How much do you want to focus each day?
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="target" className="text-sm font-medium">
                  Daily focus time goal
                </Label>
                <Select value={dailyTarget} onValueChange={setDailyTarget}>
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Select your daily goal" />
                  </SelectTrigger>
                  <SelectContent>
                    {targetOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  onClick={handleBack}
                  className="flex-1 h-11 font-medium"
                >
                  Back
                </Button>
                <Button 
                  onClick={handleComplete}
                  className="flex-1 h-11 font-medium" 
                  disabled={loading}
                >
                  {loading ? 'Setting up...' : 'Complete setup'}
                </Button>
              </div>
            </div>
          )}

          {/* Progress indicator */}
          <div className="flex justify-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${step >= 1 ? 'bg-primary' : 'bg-gray-300'}`} />
            <div className={`w-2 h-2 rounded-full ${step >= 2 ? 'bg-primary' : 'bg-gray-300'}`} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingModal;
